import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, DollarSign, Lock, AlertTriangle, FileText, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import localApiAdapter from '../../core/services/localApiAdapter';
import BrandCurrencySelect from '../../shared/components/BrandCurrencySelect';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildDefaultTreasuryConfig,
  normalizeTreasuryConfig,
  getBrandCashDenominations,
} from '../../shared/utils/cashTreasuryHelpers';
import { useSettings } from '../../core/contexts/SettingsContext';
import { syncTreasuryConfigToSettings } from './useTreasuryConfig';

const DEFAULT_CONFIG = buildDefaultTreasuryConfig();

const currencyAmountRowClass = 'flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 focus-within:ring-2 focus-within:ring-blue-500';
const currencyAmountInputClass = 'min-w-0 flex-1 border-0 bg-transparent p-0 text-white focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
const currencyAmountSuffixClass = 'shrink-0 text-sm leading-none text-white/50';

function CurrencyAmountInput({ value, onChange, suffix }) {
  return (
    <div className={currencyAmountRowClass}>
      <input
        type="number"
        value={value}
        onChange={onChange}
        className={currencyAmountInputClass}
      />
      <span className={currencyAmountSuffixClass}>{suffix}</span>
    </div>
  );
}

const TreasuryConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.caisse.pages.treasuryConfigPage.${key}`, opts);
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [treasuryConfig, setTreasuryConfig] = useState(DEFAULT_CONFIG);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await localApiAdapter.get('/treasury/config');
      const merged = normalizeTreasuryConfig({ ...DEFAULT_CONFIG, ...(data || {}) });
      setTreasuryConfig(merged);
      syncTreasuryConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Failed to load treasury config:', error);
      toast.error(t('common.load_error'));
      setTreasuryConfig(DEFAULT_CONFIG);
      syncTreasuryConfigToSettings(DEFAULT_CONFIG, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [t, patchSettings]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeTreasuryConfig(treasuryConfig);
      await localApiAdapter.put('/treasury/config', payload);
      syncTreasuryConfigToSettings(payload, patchSettings, settingsRef.current);
      setTreasuryConfig(payload);
      setSaved(true);
      toast.success(p('saved_success'));
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save treasury config:', error);
      toast.error(error.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const data = await localApiAdapter.post('/treasury/config/reset');
      const resetConfig = normalizeTreasuryConfig({ ...DEFAULT_CONFIG, ...(data || {}) });
      setTreasuryConfig(resetConfig);
      syncTreasuryConfigToSettings(resetConfig, patchSettings, settingsRef.current);
      toast.success(p('saved_success'));
    } catch (error) {
      console.error('Failed to reset treasury config:', error);
      toast.error(error.message || t('common.error'));
    }
  };

  const updateConfig = (key, value) => {
    setTreasuryConfig((prev) => {
      const next = normalizeTreasuryConfig({ ...prev, [key]: value });
      syncTreasuryConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const toggleDenomination = (value) => {
    setTreasuryConfig((prev) => {
      const current = prev.enabledDenominations || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const enabledDenominations = next.length ? next : current;
      patchSettings({ treasury: { enabledDenominations } }, { persist: false });
      return { ...prev, enabledDenominations };
    });
  };

  const currencySuffix = getBrandCurrencySuffix();
  const { banknotes, coins } = getBrandCashDenominations();

  const tabs = [
    { id: 'general', label: p('tab_general'), icon: DollarSign },
    { id: 'operations', label: p('tab_operations'), icon: FileText },
    { id: 'preferences', label: p('tab_preferences'), icon: Settings },
  ];

  const tabContent = {
    general: (
      <>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <DollarSign className="h-6 w-6" />
          {p('section_general')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_currency')}</label>
            <BrandCurrencySelect
              value={treasuryConfig.defaultCurrency}
              onChange={(e) => updateConfig('defaultCurrency', e.target.value)}
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_cash')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.maxCashAmount}
              onChange={(e) => updateConfig('maxCashAmount', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_min_cash')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.minCashAmount}
              onChange={(e) => updateConfig('minCashAmount', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_approval_above')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.requireApprovalAbove}
              onChange={(e) => updateConfig('requireApprovalAbove', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_high_cash_alert')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.highCashAlertThreshold}
              onChange={(e) => updateConfig('highCashAlertThreshold', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Lock className="h-6 w-6" />
          {p('section_closure')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_auto_closure_time')}</label>
            <input
              type="time"
              value={treasuryConfig.autoClosureTime}
              onChange={(e) => updateConfig('autoClosureTime', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_discrepancy')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.maxDiscrepancy}
              onChange={(e) => updateConfig('maxDiscrepancy', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.requireDualApproval}
              onChange={(e) => updateConfig('requireDualApproval', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_dual_approval')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.alertOnDiscrepancy}
              onChange={(e) => updateConfig('alertOnDiscrepancy', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_alert_discrepancy')}</label>
          </div>
        </div>
      </div>
      </>
    ),
    operations: (
      <>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="h-6 w-6" />
          {p('section_transfers')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_transfer')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.maxTransferAmount}
              onChange={(e) => updateConfig('maxTransferAmount', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_transfer_approval_limit')}</label>
            <CurrencyAmountInput
              value={treasuryConfig.transferApprovalLimit}
              onChange={(e) => updateConfig('transferApprovalLimit', parseInt(e.target.value, 10))}
              suffix={currencySuffix}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.requireApprovalForTransfers}
              onChange={(e) => updateConfig('requireApprovalForTransfers', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_approval_transfers')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.autoNotifyTransfers}
              onChange={(e) => updateConfig('autoNotifyTransfers', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_auto_notify_transfers')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="h-6 w-6" />
          {p('section_vouchers')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_voucher_prefix')}</label>
            <input
              type="text"
              value={treasuryConfig.voucherPrefix}
              onChange={(e) => updateConfig('voucherPrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_transfer_prefix', { defaultValue: 'بادئة التحويل' })}</label>
            <input
              type="text"
              value={treasuryConfig.transferPrefix}
              onChange={(e) => updateConfig('transferPrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_closure_prefix', { defaultValue: 'بادئة الإغلاق' })}</label>
            <input
              type="text"
              value={treasuryConfig.closurePrefix}
              onChange={(e) => updateConfig('closurePrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_reconciliation_prefix', { defaultValue: 'بادئة المطابقة' })}</label>
            <input
              type="text"
              value={treasuryConfig.reconciliationPrefix}
              onChange={(e) => updateConfig('reconciliationPrefix', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_voucher_numbering')}</label>
            <select
              value={treasuryConfig.voucherNumbering}
              onChange={(e) => updateConfig('voucherNumbering', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="auto">{p('numbering_auto')}</option>
              <option value="manual">{p('numbering_manual')}</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.requireSignature}
              onChange={(e) => updateConfig('requireSignature', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_require_signature')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6" />
          {p('section_reconciliation')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_reconciliation_frequency')}</label>
            <select
              value={treasuryConfig.reconciliationFrequency}
              onChange={(e) => updateConfig('reconciliationFrequency', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">{p('freq_daily')}</option>
              <option value="weekly">{p('freq_weekly')}</option>
              <option value="monthly">{p('freq_monthly')}</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.autoReconciliation}
              onChange={(e) => updateConfig('autoReconciliation', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_auto_reconciliation')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.alertOnMismatch}
              onChange={(e) => updateConfig('alertOnMismatch', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_alert_mismatch')}</label>
          </div>
        </div>
      </div>
      </>
    ),
    preferences: (
      <>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Bell className="h-6 w-6" />
          {p('section_notifications')}
        </h2>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.notifyOnClosure}
              onChange={(e) => updateConfig('notifyOnClosure', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_notify_closure')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.notifyOnTransfer}
              onChange={(e) => updateConfig('notifyOnTransfer', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_notify_transfer')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.notifyOnLargeTransaction}
              onChange={(e) => updateConfig('notifyOnLargeTransaction', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_notify_large')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.emailNotifications}
              onChange={(e) => updateConfig('emailNotifications', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_email_notifications')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Lock className="h-6 w-6" />
          {p('section_security')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_session_timeout')}</label>
            <input
              type="number"
              value={treasuryConfig.sessionTimeout}
              onChange={(e) => updateConfig('sessionTimeout', parseInt(e.target.value, 10))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.requirePinForOperations}
              onChange={(e) => updateConfig('requirePinForOperations', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_require_pin')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.logAllTransactions}
              onChange={(e) => updateConfig('logAllTransactions', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_log_transactions')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4">{p('section_display')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_view')}</label>
            <select
              value={treasuryConfig.defaultView}
              onChange={(e) => updateConfig('defaultView', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="journal">{p('view_journal')}</option>
              <option value="main">{p('view_main')}</option>
              <option value="monthly">{p('view_monthly')}</option>
            </select>
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_items_per_page')}</label>
            <select
              value={treasuryConfig.itemsPerPage}
              onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={treasuryConfig.showBalanceHistory}
              onChange={(e) => updateConfig('showBalanceHistory', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_show_balance_history')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <DollarSign className="h-6 w-6" />
          {p('section_denominations')}
        </h2>
        <p className="text-white/50 text-sm mb-4">{p('section_denominations_hint')}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-white/80 mb-3">{p('label_banknotes')}</h3>
            <div className="flex flex-wrap gap-2">
              {banknotes.map((note) => (
                <label
                  key={`note-${note.value}`}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                    (treasuryConfig.enabledDenominations || []).includes(note.value)
                      ? 'bg-blue-500/20 border-blue-400/50 text-white'
                      : 'bg-white/5 border-white/10 text-white/70'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={(treasuryConfig.enabledDenominations || []).includes(note.value)}
                    onChange={() => toggleDenomination(note.value)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm">{note.label}</span>
                </label>
              ))}
            </div>
          </div>
          {coins.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-white/80 mb-3">{p('label_coins')}</h3>
              <div className="flex flex-wrap gap-2">
                {coins.map((coin) => (
                  <label
                    key={`coin-${coin.value}`}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                      (treasuryConfig.enabledDenominations || []).includes(coin.value)
                        ? 'bg-blue-500/20 border-blue-400/50 text-white'
                        : 'bg-white/5 border-white/10 text-white/70'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(treasuryConfig.enabledDenominations || []).includes(coin.value)}
                      onChange={() => toggleDenomination(coin.value)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm">{coin.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        {isSaudiBrand() && (
          <p className="text-white/40 text-xs mt-4">{p('currency_suffix_hint')}</p>
        )}
      </div>
      </>
    ),
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      saved={saved}
      savedMessage={p('saved_success')}
      loading={loading}
      saving={saving}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default TreasuryConfigPage;
